"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import type { MediaAttachment } from "@/services/media-upload-service"
import { ReviewMediaUploader } from "./review-media-uploader"

type ReviewRating = 1 | 2 | 3 | 4 | 5

interface ReviewComposerProps {
  productId: number
  sessionCookie?: string
  autoFocus?: boolean
  disabled?: boolean
  onSubmit: (input: {
    rating: ReviewRating
    content: string
    attachments: MediaAttachment[]
  }) => Promise<void> | void
}

const REVIEW_RATINGS: ReviewRating[] = [5, 4, 3, 2, 1]

export function ReviewComposer({
  productId,
  sessionCookie = "",
  autoFocus = false,
  disabled = false,
  onSubmit,
}: ReviewComposerProps): React.ReactNode {
  const [rating, setRating] = useState<ReviewRating | null>(null)
  const [content, setContent] = useState("")
  const [attachments, setAttachments] = useState<MediaAttachment[]>([])

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault()

    const trimmedContent = content.trim()

    if (!rating || trimmedContent.length === 0 || disabled) {
      return
    }

    console.log(
      "[Review Composer] Submitting review with attachments:",
      attachments
    )
    await onSubmit({ content: trimmedContent, rating, attachments })
    setContent("")
    setRating(null)
    setAttachments([])
  }

  return (
    <form
      className='space-y-4 rounded-lg border bg-background p-4'
      onSubmit={handleSubmit}
    >
      <fieldset className='space-y-2'>
        <legend className='font-medium text-sm'>Sua nota</legend>

        <div className='flex flex-wrap gap-2'>
          {REVIEW_RATINGS.map((value) => {
            const inputId = `product-review-rating-${value}`

            return (
              <label
                className='flex cursor-pointer items-center gap-2 text-sm'
                htmlFor={inputId}
                key={value}
              >
                <input
                  checked={rating === value}
                  disabled={disabled}
                  id={inputId}
                  name='rating'
                  onChange={() => setRating(value)}
                  type='radio'
                  value={value}
                />
                {value} estrelas
              </label>
            )
          })}
        </div>
      </fieldset>

      <div className='space-y-2'>
        <label className='font-medium text-sm' htmlFor='product-review-content'>
          Sua avaliacao
        </label>

        <textarea
          aria-label='Sua avaliacao'
          autoFocus={autoFocus}
          className='min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none ring-ring/50 transition-shadow focus-visible:ring-[3px]'
          disabled={disabled}
          id='product-review-content'
          onChange={(event) => setContent(event.target.value)}
          placeholder='Conte como foi sua experiencia com este produto.'
          value={content}
        />
      </div>

      <div className='space-y-2'>
        <label className='font-medium text-sm'>Fotos e videos (opcional)</label>
        <ReviewMediaUploader
          attachments={attachments}
          disabled={disabled}
          onAttachmentsChange={setAttachments}
          productId={productId}
          sessionCookie={sessionCookie}
        />
      </div>

      <Button disabled={disabled} type='submit'>
        Enviar avaliacao
      </Button>
    </form>
  )
}
